/* eslint-disable max-lines */
import EXIF from './exif'
import { message as $message } from 'antd'
import request from 'umi-request'

import { browserInfo } from '@/utils/utils'
import { compareVersion } from '@/utils/system'

/*
 * Q: 安卓出现无法取到文件类型
 * A: 临时解决方式，非视频类型全部走图片类型
 * PS:  通过二进制文件流读读取文件类型：https://segmentfault.com/q/1010000009224378
 * */

export const toast = $message

export const getSuffix = filename => {
    const pos = filename.lastIndexOf('.')

    let suffix = ''

    if (pos !== -1) {
        suffix = filename.substring(pos)
    }
    return suffix
}

// 获取文件类型
export const getFileType = file => file.type

// 格式化文件size
export const formatSize = size => {
    return size / 1024 > 1024 ? `${~~((10 * size) / 1024 / 1024) / 10}MB` : `${~~(size / 1024)}KB`
}

// 获取图片类型
export const getImgType = (src, config) => {
    const nojfifIsJpeg = config?.nojfifIsJpeg

    const imageReg = /\.(png|jpeg|jpg|gif|bmp|webp)/
    const arr = imageReg.exec(src)

    let type = 'jpeg'

    if (arr && arr[1]) {
        type = arr[1]
    } else {
        // 处理不支持的格式
        const suffix = String(src).split('.')
        if (suffix.length > 1) {
            type = suffix.pop()
        }
    }
    // .jfif 也是jpeg
    if (!nojfifIsJpeg && type === 'jfif') {
        type = 'jpeg'
    }

    return `image/${type}`
}

// 转化 dataUr l为 bloc
export const transDataUrlToBlob = dataUrl => {
    const arr = dataUrl.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const str = atob(arr[1])
    let n = str.length
    const u8arr = new Uint8Array(n)
    while (n--) {
        u8arr[n] = str.charCodeAt(n)
    }
    return new Blob([u8arr], { type: mime })
}

export const transCanvasToBlob = (cvs, type, encoderOptions) => {
    return new Promise(resolve => {
        cvs.toBlob(resolve, type, encoderOptions)
    })
}

export const transBlobToFile = (blob, name, type) => {
    try {
        return new File([blob], name, { type })
    } catch (e) {
        console.error('catchError', e)
        blob.lastModifiedDate = new Date()
        blob.name = name

        return blob
    }
}

// 是否是视频
export const isVideo = file => /^video\/.+$/.test(file.type)

// 是否是图片
export const isImage = file => /^image\/.+$/.test(file.type)

// 延时
// eslint-disable-next-line no-promise-executor-return
export const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

// 加载文件
export const loadFile = src => {
    return new Promise((resolve, reject) => {
        const img = new Image()

        img.crossOrigin = 'Anonymous'

        img.onload = () => {
            resolve(img)
        }
        img.onerror = () => {
            reject(new Error('加载文件失败'))
        }
        img.src = src
    })
}

export const hackNewBrowser = orientation => {
    if (browserInfo.name === 'chrome') {
        return compareVersion(browserInfo.version, '81') >= 0 ? 1 : orientation
    }

    if (browserInfo.name === 'edge') {
        return compareVersion(browserInfo.version, '81') >= 0 ? 1 : orientation
    }

    if (browserInfo.name === 'firefox') {
        return compareVersion(browserInfo.version, '26') >= 0 ? 1 : orientation
    }

    if (browserInfo.name === 'opera') {
        return compareVersion(browserInfo.version, '67') >= 0 ? 1 : orientation
    }

    if (browserInfo.name === 'safari') {
        return compareVersion(browserInfo.version, '13.1') >= 0 ? 1 : orientation
    }

    if (['qq', 'sogou', 'ie'].includes(browserInfo.name)) {
        return orientation
    }

    return 1
}

// 读取文件
export const readFile = source => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => {
            loadFile(e.target.result).then(img => {
                EXIF.getData(source, function () {
                    const orientation = hackNewBrowser(EXIF.getTag(this, 'Orientation') || 1)

                    resolve({ img, src: img.src, orientation })
                })
            })
        }
        reader.onerror = () => {
            reject(new Error('读取文件失败'))
        }
        reader.readAsDataURL(source)
    })
}

// 获取视频信息
export const getVideoMetaData = source => {
    const reader = new FileReader()

    return new Promise((resolve, reject) => {
        try {
            reader.readAsDataURL(source)

            reader.onload = () => {
                const video = document.createElement('video')
                video.src = reader.result
                video.preload = 'metadata'
                video.onloadedmetadata = function () {
                    const duration = video.duration
                    const width = video.videoWidth
                    const height = video.videoHeight

                    resolve({ duration, width, height })
                }
            }
        } catch (e) {
            console.error(e)
            reject(e)
        }
    })
}

// 获取图片尺寸
export const getImgRect = file => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = async e => {
            try {
                const img = await loadFile(e.target.result)

                resolve({ naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight })
            } catch (e) {
                console.error('catchError', e)
                reject(e)
            }
        }

        reader.onerror = () => {
            reject(new Error('获取图片尺寸失败'))
        }

        reader.readAsDataURL(file)
    })
}

// 获取文件尺寸
export const getFileRect = file => {
    // 图片文件
    if (isImage(file)) {
        return getImgRect(file)
    }

    return Promise.reject(new Error('无匹配类型'))
}

export const WHITE_PREVIEW_TYPE = ['image/gif']

// 调整图片方向+压缩
export const adjustImg = async (img, { type, orientation }) => {
    const cvs = document.createElement('canvas')
    const ctx = cvs.getContext('2d')

    let width = img.width
    let height = img.height

    let naturalW = width
    let naturalH = height

    switch (orientation) {
        case 5:
        case 6:
        case 7:
        case 8:
            naturalW = height
            naturalH = width
            break
    }

    cvs.width = naturalW
    cvs.height = naturalH

    switch (orientation) {
        case 2: // horizontal flip
            ctx.translate(width, 0)
            ctx.scale(-1, 1)
            break

        case 3: // 180 rotate left
            ctx.translate(width, height)
            ctx.rotate(Math.PI)
            break

        case 4: // vertical flip
            ctx.translate(0, height)
            ctx.scale(1, -1)
            break

        case 5: // vertical flip + 90 rotate right
            ctx.rotate((90 * Math.PI) / 180)
            ctx.scale(1, -1)
            break

        case 6: // 90 rotate right
            ctx.rotate((90 * Math.PI) / 180)
            ctx.translate(0, -height)
            break

        case 7: // horizontal flip + 90 rotate right
            ctx.rotate(0.5 * Math.PI)
            ctx.translate(width, -height)
            ctx.scale(-1, 1)
            break

        case 8: // 90 rotate left
            ctx.rotate(-0.5 * Math.PI)
            ctx.translate(-width, 0)
            break
    }

    ctx.drawImage(img, 0, 0, width, height)

    const quantity = 0.9 // 1：255k => 325k；0.9 255k => 74k

    const url = cvs.toDataURL(type, quantity)

    const blob = await transCanvasToBlob(cvs, type, quantity)

    return {
        url,
        base64: url,
        blob,
        width,
        height,
    }
}

// 生成缩略图
export const makeThumb = async (img, opts) => {
    // const img = await loadFile(url)

    const thumbCvs = document.createElement('canvas')
    const thumbCtx = thumbCvs.getContext('2d')

    let naturalW = img.width
    let naturalH = img.height
    let thumbW = opts.width * 2
    let thumbH = opts.height * 2
    let scale
    let w
    let h
    let x
    let y

    // 如果 width/height 的值介于 0 - 1, 说明设置的是百分比。
    if (thumbW <= 1 && thumbW > 0) {
        thumbW = naturalW * thumbW
    }
    if (thumbH <= 1 && thumbH > 0) {
        thumbH = naturalH * thumbH
    }

    scale = Math[opts.crop ? 'max' : 'min'](thumbW / naturalW, thumbH / naturalH)

    // 不允许放大。
    opts.allowMagnify || (scale = Math.min(1, scale))

    w = naturalW * scale
    h = naturalH * scale

    if (opts.crop) {
        thumbCvs.width = thumbW
        thumbCvs.height = thumbH

        x = (thumbCvs.width - w) / 2
        y = (thumbCvs.height - h) / 2
    } else {
        thumbCvs.width = w
        thumbCvs.height = h

        x = 0
        y = 0
    }

    thumbCtx.drawImage(img, x, y, w, h)

    const quality = opts.quality / 100

    const thumbUrl = thumbCvs.toDataURL(opts.type, quality)

    const blob = await transCanvasToBlob(thumbCvs, opts.type, quality)

    return {
        url: thumbUrl,
        base64: thumbUrl,
        blob,
    }
}

// 获取阿里云网络图信息
export const getOssUrlInfo = pic => {
    if (/\/\/images.sursung.com/.test(pic)) {
        return new Promise((resolve, reject) => {
            request
                .get(`${pic}?x-oss-process=image/info`)
                .then(response => {
                    let imageWidth = response?.ImageWidth?.value
                    let imageHeight = response?.ImageHeight?.value

                    // 特殊拍摄角，狂宽高颠倒
                    const specialOrientation = ['5', '6', '7', '8']
                    if (specialOrientation.includes(response?.Orientation?.value)) {
                        imageWidth = response?.ImageHeight?.value
                        imageHeight = response?.ImageWidth?.value
                    }

                    const info = {
                        size: response?.FileSize?.value,
                        formatSize: formatSize(response?.FileSize?.value),
                        format: response?.Format?.value,
                        imageHeight,
                        imageWidth,
                    }
                    resolve(info)
                })
                .catch(error => {
                    resolve(false)
                })
        })
    } else {
        return Promise.resolve(false)
    }
}

// 获取图片url信息
export const getImageInfoByUrl = url => {
    if (/\/\/images.sursung.com/.test(url)) {
        return getOssUrlInfo(url)
    }

    return new Promise(resolve => {
        request
            .get(url, {
                responseType: 'blob',
            })
            .then(blob => {
                loadFile(url)
                    .then(img => {
                        resolve({
                            imageWidth: img.width,
                            imageHeight: img.height,
                            size: blob.size,
                            formatSize: formatSize(blob.size),
                        })
                    })
                    .catch(() => {
                        resolve(false)
                    })
            })
            .catch(() => {
                resolve(false)
            })
    })
}

// 获取图片url携带的信息（url中有_s?_h?_w?格式获取的信息）
export const getImageInfoFromUrl = (url, config) => {
    if (!/\/\/images.sursung.com/.test(url)) {
        return null
    }

    const reg = /(_s(\d+(\.\d+)?)_h(\d+(\.\d+)?)_w(\d+(\.\d+)?)\.)/
    const params = url.match(reg)
    const size = Array.isArray(params) ? params[2] : undefined
    const height = Array.isArray(params) ? params[4] : undefined
    const width = Array.isArray(params) ? params[6] : undefined

    if (size && height && width) {
        return {
            size: parseFloat(size, 10),
            height: parseFloat(height, 10),
            width: parseFloat(width, 10),
            imgType: getImgType(url, config),
            formatSize: formatSize(parseFloat(size, 10)),
        }
    }
    return null
}
